import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  fromEvent,
  map,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { PulseWidgetService } from './pulse-widget.service';

/** How often the "needs your reply" count is refreshed while the tab is in view. */
export const NEEDS_REPLY_POLL_MS = 3 * 60 * 1000;

export type MyTicketsFilter = '' | 'needs_reply' | 'in_progress' | 'waiting_on_supplier' | 'resolved';

/** A support conversation on My tickets. */
export interface MyTicket {
  uuid: string;
  reference_number: string;
  subject: string;
  booking_ref: string;
  product: string;
  issue_type: string;
  status: string;
  status_category: 'open' | 'waiting' | 'resolved' | string;
  created_at: string;
  updated_at: string;
  last_message: string;
  last_from: 'agent' | 'contact' | '';
  needs_reply: boolean;
  own: boolean;
  joined: boolean;
  /** Owners only: who started it. */
  raised_by?: string;
}

export interface MyTicketsSummary {
  open: number;
  needs_reply: number;
  waiting_on_supplier: number;
  resolved_30d: number;
  median_first_reply_seconds: number | null;
}

export interface MyTicketTranscript {
  uuid: string;
  reference_number: string;
  subject: string;
  created_at: string;
  transcript: string;
}

export interface MyTicketsPage {
  enabled: boolean;
  owner?: boolean;
  items?: MyTicket[];
  total?: number;
  page?: number;
  page_size?: number;
  summary?: MyTicketsSummary;
}

/**
 * My tickets: the user's ETH Pulse conversations (an owner's: their organization's, a franchise
 * owner's: every linked organization's), as eth-api lets them see. Also keeps the "needs your reply"
 * count for the top bar.
 */
@Injectable({ providedIn: 'root' })
export class PulseMyTicketsService {
  private needsReplySubject = new BehaviorSubject<number>(0);
  private countRefreshedAt = 0;

  /** Conversations where support replied and is waiting on the user's side. */
  readonly needsReply$: Observable<number> = this.needsReplySubject.asObservable();

  constructor(
    private http: HttpClient,
    private pulseWidget: PulseWidgetService,
  ) {}

  list(filter: MyTicketsFilter = '', bookingRef = '', page = 1): Observable<MyTicketsPage> {
    let params = new HttpParams().set('page', String(page));
    if (filter) params = params.set('filter', filter);
    if (bookingRef.trim()) params = params.set('booking_ref', bookingRef.trim());
    return this.http.get<MyTicketsPage>(`${environment.apiUrl}pulse/my-tickets`, { params }).pipe(
      tap((res) => {
        if (res?.enabled && res.summary) this.needsReplySubject.next(res.summary.needs_reply || 0);
      }),
    );
  }

  /**
   * Keep the "needs your reply" count fresh while the chat widget is active, and only while the tab
   * is in view. A tab brought back refreshes at once only if the count is older than the poll
   * interval, so switching tabs costs no extra calls. Unsubscribe to stop.
   */
  watchNeedsReply(): Subscription {
    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => document.visibilityState !== 'hidden'),
      distinctUntilChanged(),
    );
    return combineLatest([this.pulseWidget.active$.pipe(distinctUntilChanged()), visible$])
      .pipe(
        switchMap(([active, visible]) => {
          if (!active) {
            this.countRefreshedAt = 0;
            this.reset();
            return EMPTY;
          }
          if (!visible) return EMPTY;
          const due = Math.max(this.countRefreshedAt + NEEDS_REPLY_POLL_MS - Date.now(), 0);
          return timer(due, NEEDS_REPLY_POLL_MS);
        }),
      )
      .subscribe(() => {
        this.countRefreshedAt = Date.now();
        this.refreshCount();
      });
  }

  /** Refresh only the "needs your reply" count. */
  refreshCount(): void {
    this.list().subscribe({
      next: (res) => {
        if (!res?.enabled) this.needsReplySubject.next(0);
      },
      error: () => {},
    });
  }

  /**
   * Open a conversation in the chat widget. An owner opening a colleague's conversation joins it
   * first, so they can read and reply from their own widget.
   */
  async open(uuid: string): Promise<void> {
    await firstValueFrom(this.http.post(`${environment.apiUrl}pulse/my-tickets/${encodeURIComponent(uuid)}/open`, {}));
    this.pulseWidget.openConversation(uuid);
  }

  reset(): void {
    this.needsReplySubject.next(0);
  }

  /** Download a conversation's transcript as a PDF. */
  async downloadTranscript(uuid: string): Promise<void> {
    const data = await firstValueFrom(
      this.http.get<MyTicketTranscript>(`${environment.apiUrl}pulse/my-tickets/${encodeURIComponent(uuid)}/transcript`),
    );
    const { default: JsPDF } = await import('jspdf');
    const doc = new JsPDF({ unit: 'pt', format: 'a4' });
    writeTranscriptPdf(doc, data);
    doc.save(transcriptFileName(data.reference_number));
  }
}

/** "about 15 min", "about 2 h" for a duration in seconds; '' when unknown. */
export function replyTimeLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 90) return 'about 1 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `about ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `about ${hours} h`;
  return `about ${Math.round(hours / 24)} days`;
}

/** File name for a transcript PDF, e.g. support-conversation-42.pdf. */
export function transcriptFileName(reference: string): string {
  const safe = String(reference || '').replace(/[^A-Za-z0-9_-]/g, '') || 'transcript';
  return `support-conversation-${safe}.pdf`;
}

/** Minimal jsPDF surface used for the transcript, so it can be tested without a real PDF. */
export interface TranscriptPdf {
  setFont(name: string, style: string): unknown;
  setFontSize(size: number): unknown;
  text(text: string | string[], x: number, y: number): unknown;
  splitTextToSize(text: string, width: number): string[];
  addPage(): unknown;
}

/** Lays the transcript out on A4 pages with a title, wrapping long lines. */
export function writeTranscriptPdf(doc: TranscriptPdf, data: MyTicketTranscript): void {
  const margin = 48;
  const width = 595 - margin * 2;
  const bottom = 842 - margin;
  const lineHeight = 14;
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  const title = `Support conversation #${data.reference_number}${data.subject ? ' – ' + data.subject : ''}`;
  for (const line of doc.splitTextToSize(title, width)) {
    doc.text(line, margin, y);
    y += 18;
  }
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  for (const paragraph of String(data.transcript || '').split('\n')) {
    const lines = paragraph ? doc.splitTextToSize(paragraph, width) : [''];
    for (const line of lines) {
      if (y > bottom) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }
}
